import { AlertTriangle, Mail, CheckCircle, Loader2 } from "lucide-react"
import { useState } from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"
import { useAuth } from "@/auth/useAuth"
import { validateEmail } from "@/lib/validation"
import { safeError } from "@/utils/safeError"

interface MagicLinkFormProps {
  onSuccess?: () => void
  // When set, only emails on this domain may request a link (e.g. "condri.app"
  // for staff login). The real access gate is still the staff/admin role — this
  // is a first-line filter so non-staff don't create stray accounts.
  allowedEmailDomain?: string
}

export const MagicLinkForm = ({
  onSuccess,
  allowedEmailDomain,
}: MagicLinkFormProps) => {
  const [email, setEmail] = useState("")
  const [emailSent, setEmailSent] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const {
    requestMagicLink,
    isLoading,
    error: authError,
    clearError,
  } = useAuth()
  const { toast } = useToast()

  // Clear errors when user types
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (authError || localError) {
      clearError()
      setLocalError(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)

    // Validate email
    const emailValidation = validateEmail(email)
    if (!emailValidation.isValid) {
      setLocalError(emailValidation.error!)
      return
    }

    if (
      allowedEmailDomain &&
      !email.trim().toLowerCase().endsWith(`@${allowedEmailDomain}`)
    ) {
      setLocalError(`Staff login is restricted to @${allowedEmailDomain} email addresses.`)
      return
    }

    try {
      await requestMagicLink(email)
      setEmailSent(true)
      toast({
        title: "Magic link sent!",
        description: `Check your email at ${email} for a login link.`,
      })
      onSuccess?.()
    } catch (error) {
      console.error("Magic link request failed:", safeError(error))
      // Error is handled by AuthService and stored in authError
    }
  }

  const error = authError || localError

  if (emailSent) {
    return (
      <div className="space-y-4 text-center">
        <div className="mx-auto w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-success" />
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-foreground">
            Check your email
          </h3>
          <p className="text-muted-foreground">
            We've sent a magic link to <strong>{email}</strong>
          </p>
          <p className="text-sm text-muted-foreground">
            Click the link in your email to sign in instantly. The link will
            expire in 15 minutes.
          </p>
        </div>

        <div className="pt-4">
          <Button
            variant="outline"
            onClick={() => {
              setEmailSent(false)
              setEmail("")
            }}
            className="w-full"
          >
            Send to a different email
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {typeof error === "string" ? error : error.message}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="email">Email address</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={handleEmailChange}
            required
            disabled={isLoading}
            className="pl-10 transition-all duration-200 focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          We'll send you a secure login link.
        </p>
      </div>

      <Button
        type="submit"
        className="w-full bg-gradient-hero hover:bg-gradient-hero/90 text-white font-medium py-3 rounded-lg transition-all duration-300 shadow-soft hover:shadow-glow"
        disabled={isLoading || !email.trim()}
        size="lg"
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending magic link...
          </>
        ) : (
          <>
            <Mail className="mr-2 h-4 w-4" />
            Send magic link
          </>
        )}
      </Button>
    </form>
  )
}
